/**
 * <load-more-products> — replaces numbered pagination with a "Show more" button
 * that appends the next page's grid items in place.
 *
 * The next page is fetched as a section render (`?section_id=…`) so the promo /
 * USP blocks and their absolute grid positions come straight from Liquid; nothing
 * about the grid is rebuilt client side.
 */
class LoadMoreProducts extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.button = this.querySelector('[data-load-more]');
    this.button?.addEventListener('click', this.onClick.bind(this));
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get nextUrl() {
    return this.dataset.nextUrl;
  }

  async onClick() {
    const url = this.nextUrl;
    if (!url || this.loading) return;

    this.loading = true;
    this.button.setAttribute('aria-busy', 'true');

    try {
      const separator = url.includes('?') ? '&' : '?';
      const response = await fetch(`${url}${separator}section_id=${this.sectionId}`);
      if (!response.ok) throw new Error(response.statusText);

      const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.append(parsed);
    } catch (error) {
      console.error(error);
      // Leave the button enabled so the shopper can retry; a failed append must
      // not strand them on a half-loaded grid with no way forward.
    } finally {
      this.loading = false;
      this.button?.removeAttribute('aria-busy');
    }
  }

  append(parsed) {
    const grid = document.getElementById('product-grid');
    const incoming = parsed.getElementById('product-grid');
    if (!grid || !incoming) return;

    const firstNew = incoming.firstElementChild;
    const addedProducts = incoming.querySelectorAll('[data-grid-product]').length;
    while (incoming.firstElementChild) {
      grid.appendChild(incoming.firstElementChild);
    }
    this.updateProgress(addedProducts);

    const nextLoader = parsed.querySelector('load-more-products');
    const nextUrl = nextLoader?.dataset.nextUrl;

    if (nextUrl) {
      this.dataset.nextUrl = nextUrl;
    } else {
      // Everything is loaded: the button goes, the count and bar stay at 100%.
      delete this.dataset.nextUrl;
      this.button?.remove();
      this.button = null;
    }

    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(grid.innerHTML);
    }

    // Move focus to the first appended card so keyboard and screen-reader users
    // continue from the new content instead of the top of the grid.
    const focusTarget = firstNew?.querySelector('a, button');
    focusTarget?.focus({ preventScroll: true });
  }

  /** Moves the "shown / total" count and bar on by the cards just appended. */
  updateProgress(added) {
    const total = Number(this.dataset.total) || 0;
    const shown = Math.min((Number(this.dataset.shown) || 0) + added, total);
    this.dataset.shown = shown;

    const count = this.querySelector('[data-progress-shown]');
    if (count) count.textContent = shown;

    const bar = this.querySelector('[role="progressbar"]');
    if (bar) {
      bar.setAttribute('aria-valuenow', shown);
      bar.setAttribute('aria-valuetext', `${shown}/${total}`);
    }

    const fill = this.querySelector('.collection-grid__progress-fill');
    if (fill) fill.style.setProperty('--progress', `${total ? (shown / total) * 100 : 0}%`);
  }
}

if (!customElements.get('load-more-products')) customElements.define('load-more-products', LoadMoreProducts);

